import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Avatar,
  Stack,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import { brandsApi } from '../../utils/apiClient';

const AdminBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    isActive: true
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await brandsApi.getAll();
      // Backend returns array directly, not wrapped in data property
      setBrands(Array.isArray(response) ? response : (response?.data || []));
      setError(null);
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (brand = null) => {
    if (brand) {
      setEditingBrand(brand);
      setFormData({
        name: brand.name,
        description: brand.description || '',
        website: brand.website || '',
        isActive: brand.isActive
      });
      // Show existing logo if available
      if (brand.logo) {
        if (typeof brand.logo === 'object' && brand.logo.url) {
          setLogoPreview(brand.logo.url);
        } else if (typeof brand.logo === 'string') {
          setLogoPreview(brand.logo);
        }
      }
    } else {
      setEditingBrand(null);
      setFormData({
        name: '',
        description: '',
        website: '',
        isActive: true
      });
      setLogoPreview(null);
    }
    setLogoFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBrand(null);
    setFormData({
      name: '',
      description: '',
      website: '',
      isActive: true
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('website', formData.website);
      submitData.append('isActive', formData.isActive);
      
      if (logoFile) {
        submitData.append('logo', logoFile);
      }

      if (editingBrand) {
        await brandsApi.update(editingBrand._id, submitData);
      } else {
        await brandsApi.create(submitData);
      }

      await fetchBrands();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving brand:', err);
      setError(err.response?.data?.message || 'Failed to save brand');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;
    
    try {
      await brandsApi.delete(id);
      await fetchBrands();
    } catch (err) {
      console.error('Error deleting brand:', err);
      setError(err.response?.data?.message || 'Failed to delete brand');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await brandsApi.toggleActive(id);
      await fetchBrands();
    } catch (err) {
      console.error('Error toggling brand status:', err);
      setError('Failed to update brand status');
    }
  };

  const renderLogo = (logo) => {
    if (!logo) return <BrandingWatermarkIcon />;
    
    let logoUrl = null;
    if (typeof logo === 'object' && logo.url) {
      logoUrl = logo.url;
    } else if (typeof logo === 'string') {
      logoUrl = logo;
    }
    
    return logoUrl ? <Avatar src={logoUrl} variant="rounded" /> : <BrandingWatermarkIcon />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} mb={3} gap={1}>
        <Typography variant="h4" fontWeight={700}>
          Brand Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Brand
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell>Logo</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No brands found. Add your first brand!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              brands.map((brand) => (
                <TableRow key={brand._id}>
                  <TableCell>
                    {renderLogo(brand.logo)}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{brand.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {brand.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {brand.website ? (
                      <a href={brand.website} target="_blank" rel="noopener noreferrer">
                        {brand.website}
                      </a>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={brand.isActive ? 'Active' : 'Inactive'}
                      color={brand.isActive ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(brand._id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleOpenDialog(brand)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(brand._id)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBrand ? 'Edit Brand' : 'Add New Brand'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Brand Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              fullWidth
            />
            
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
            />
            
            <TextField
              label="Website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://example.com"
              fullWidth
            />

            <Box>
              <Button variant="outlined" component="label" fullWidth>
                Upload Logo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </Button>
              {logoPreview && (
                <Box mt={2} display="flex" justifyContent="center">
                  <Avatar
                    src={logoPreview}
                    variant="rounded"
                    sx={{ width: 120, height: 120 }}
                  />
                </Box>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || submitting}
          >
            {submitting ? <CircularProgress size={24} /> : (editingBrand ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminBrands;
